
import axios from 'axios';
import { Notification } from '@/contexts/NotificationContext';

// Base URL for the API - can be configured based on environment
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Create axios instance with better error handling
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors more gracefully
    if (!error.response) {
      console.error('Network Error:', error.message);
      return Promise.reject(new Error('Network error. Please check your connection.'));
    }
    
    // Log errors to console
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Fetch all notifications for a user
export const fetchUserNotifications = async (userId: string): Promise<Notification[]> => {
  try {
    const response = await api.get(`/notifications?userId=${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return []; // Return empty array instead of throwing to prevent UI crashes
  }
};

// Mark a notification as read
export const markNotificationAsRead = async (id: string, userId: string): Promise<void> => {
  try {
    await api.put(`/notifications/${id}/read`, { userId });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

// Mark all notifications as read for a user
export const markAllNotificationsAsRead = async (userId: string): Promise<void> => {
  try {
    await api.put(`/notifications/read-all`, { userId });
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

// Create a new notification
export const createNotification = async (
  notification: Omit<Notification, 'id' | 'createdAt' | 'isRead'>
): Promise<Notification> => {
  try {
    const response = await api.post('/notifications', notification);
    return response.data;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

// Delete a notification
export const deleteNotification = async (id: string, userId: string): Promise<void> => {
  try {
    await api.delete(`/notifications/${id}`, { data: { userId } });
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

// Get unread notification count
export const getUnreadNotificationCount = async (userId: string): Promise<number> => {
  try {
    const response = await api.get(`/notifications/unread-count?userId=${userId}`);
    return response.data.count;
  } catch (error) {
    console.error('Error fetching unread notification count:', error);
    return 0; // Return 0 instead of throwing to prevent UI crashes
  }
};

export default {
  fetchUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  createNotification,
  deleteNotification,
  getUnreadNotificationCount
};
